/**
 * Registration source (UTM, letter code, click IDs) — the single parser shared by
 * the wizard payload (`source` in register/registerDirect) and the GA4 events.
 *
 * Reads the allowlisted params from the URL on first entry, keeps them in
 * sessionStorage (first wins within the session) and hands them over as the
 * nested `RegistrationSourceDto` (contract.md p. 3). Values are sent raw: the
 * backend sanitizes (sanitize-or-null). Storage failures mean "no source".
 *
 * @see docs/specifications/2026-10-28-zrodlo-rejestracji-utm/contract.md
 */

import { cookieService } from "../services/cookieService";

export const REGISTRATION_SOURCE_STORAGE_KEY = "registrationSource";

/** Shape of `RegistrationSourceDto` on the wire (contract.md p. 3). */
export interface RegistrationSourcePayload {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  gclid?: string;
  ref?: string;
  landingPath?: string;
  referrerHost?: string;
  firstSeenAt?: string;
}

/** URL param -> DTO field (allowlist). */
const PARAM_TO_FIELD: Record<string, keyof RegistrationSourcePayload> = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
  utm_term: "utmTerm",
  fbclid: "fbclid",
  gclid: "gclid",
  ref: "ref",
  landingPath: "landingPath",
  referrerHost: "referrerHost",
  fs: "firstSeenAt",
};

const MAX_VALUE_LENGTH = 512;

/** Reads the source params from `search` and stores them unless a source is already stored. */
export function captureRegistrationSource(search: string = window.location.search): void {
  try {
    if (sessionStorage.getItem(REGISTRATION_SOURCE_STORAGE_KEY) !== null) return;

    const params = new URLSearchParams(search);
    // ePrivacy (D-2): ad click IDs reach the device only with marketing consent.
    const marketingConsent = cookieService.hasMarketingConsent();
    const source: RegistrationSourcePayload = {};
    for (const [param, field] of Object.entries(PARAM_TO_FIELD)) {
      if (!marketingConsent && (field === "fbclid" || field === "gclid")) continue;
      const value = params.get(param); // first value of a repeated param
      if (value) source[field] = value.slice(0, MAX_VALUE_LENGTH);
    }
    if (Object.keys(source).length === 0) return;

    sessionStorage.setItem(REGISTRATION_SOURCE_STORAGE_KEY, JSON.stringify(source));
  } catch {
    // sessionStorage unavailable: register without a source.
  }
}

/**
 * Stored source for the registration request, or undefined. Click IDs
 * (`fbclid`/`gclid`) are included only with marketing consent (D-C).
 */
export function getRegistrationSource(options: {
  marketingConsent: boolean;
}): RegistrationSourcePayload | undefined {
  try {
    const raw = sessionStorage.getItem(REGISTRATION_SOURCE_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return undefined;

    const source: RegistrationSourcePayload = {};
    for (const field of Object.values(PARAM_TO_FIELD)) {
      if (!options.marketingConsent && (field === "fbclid" || field === "gclid")) continue;
      const value = (parsed as Record<string, unknown>)[field];
      if (typeof value === "string" && value) source[field] = value;
    }
    return Object.keys(source).length > 0 ? source : undefined;
  } catch {
    return undefined;
  }
}
